/**
 * Chart-settings store — per-mode chip/line visibility for the
 * Portfolio vs SPY chart.
 *
 * Two axes of visibility per comparison line:
 *   • `enabled` — the chip AND the line both exist. Flipped via the
 *     Graph Settings dialog switches. OFF removes the chip from the
 *     hero strip entirely and drops the line from the chart.
 *   • `active`  — while the chip is visible, whether its line is
 *     drawn. Flipped by clicking the chip itself. OFF leaves the chip
 *     in the strip (dimmed) but removes the line.
 *
 * Final visibility = enabled && active. The chart component reads the
 * derived `show*` booleans from a selector.
 *
 * Settings are stored PER MODE (`pct` and `usd`) so the two chart
 * types are independent: enabling SPY in % does not enable it in $.
 * ND is $-only (the stepped capital-held line is meaningless as a
 * percentage), so it lives only on the `usd` side.
 *
 * Persisted to localStorage under `whb-chart-settings` so user choices
 * survive page reloads and logouts.
 */
import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";

export type ChartMode = "pct" | "usd";
export type LineKey =
  | "spy"
  | "spyDiv"
  | "target"
  | "cashflow"
  | "netDeposits"
  | "sma";

/** Window-resolution mode for the SMA overlay.
 *  - "auto"  → window length picked from the active chart range
 *               (smaller windows for shorter ranges, larger for ALL).
 *               See `resolveSmaWindow` in chart-math for the table.
 *  - "fixed" → use `smaWindowDays` regardless of range. Lets users
 *               compare the SAME smoothing window across views, which
 *               is the point of an SMA in technical analysis. */
export type SmaWindowMode = "auto" | "fixed";

/** Discrete preset windows offered in the SMA picker. The picker
 *  also accepts a Custom value (any int 2–500) — these are the
 *  one-tap options. */
export const SMA_PRESETS: readonly number[] = [5, 10, 20, 30, 50, 100, 200];

export interface LineState {
  enabled: boolean;
  active: boolean;
}

type ModeState = Record<LineKey, LineState>;

interface ChartSettingsState {
  pct: ModeState;
  usd: ModeState;
  /**
   * When true, the chart's centered date-label slot shows the active
   * range label ("Past year" / "All time" / custom range) while the
   * user is NOT hovering the chart. When the user hovers a specific
   * date, the hover date always wins. Off by default — the slot is
   * empty at rest. Added Apr-2026 R12-11.
   */
  showSelectedRange: boolean;
  setShowSelectedRange: (v: boolean) => void;
  /** SMA window-resolution mode. Shared between $ and % views — a
   *  user comparing two modes wants the SAME smoothing window in both.
   *  Sergey 2026-04-30. */
  smaWindowMode: SmaWindowMode;
  /** Custom window length when `smaWindowMode === "fixed"`. Persisted
   *  even while in "auto" so flipping back-and-forth restores the user's
   *  previous fixed pick. Defaults to 30. */
  smaWindowDays: number;
  setSmaWindow: (mode: SmaWindowMode, days?: number) => void;
  setEnabled: (mode: ChartMode, key: LineKey, enabled: boolean) => void;
  setActive: (mode: ChartMode, key: LineKey, active: boolean) => void;
  toggleActive: (mode: ChartMode, key: LineKey) => void;
  setAll: (mode: ChartMode, enabled: boolean) => void;
}

const defaultLine = (enabled: boolean): LineState => ({ enabled, active: true });

// V2-era defaults: the minimalist hero starts with only Cashflow
// dots; users opt into SPY / Target / SPY+D / ND / SMA via Graph
// Settings. SMA is OFF by default in both modes per Sergey 2026-04-30.
const defaultPct: ModeState = {
  spy: defaultLine(false),
  spyDiv: defaultLine(false),
  target: defaultLine(false),
  cashflow: defaultLine(true),
  // ND not applicable in % mode — kept here with enabled:false so the
  // shape stays symmetric. The chart component never reads it in pct.
  netDeposits: defaultLine(false),
  sma: defaultLine(false)
};

const defaultUsd: ModeState = {
  spy: defaultLine(false),
  spyDiv: defaultLine(false),
  target: defaultLine(false),
  cashflow: defaultLine(true),
  netDeposits: defaultLine(false),
  sma: defaultLine(false)
};

export const useChartSettingsStore = create<ChartSettingsState>()(
  persist(
    (set) => ({
      pct: defaultPct,
      usd: defaultUsd,
      showSelectedRange: false,
      setShowSelectedRange: (v) => set({ showSelectedRange: v }),
      smaWindowMode: "auto",
      smaWindowDays: 30,
      setSmaWindow: (mode, days) =>
        set((s) => ({
          smaWindowMode: mode,
          smaWindowDays:
            typeof days === "number" && Number.isFinite(days) && days > 1
              ? Math.min(500, Math.round(days))
              : s.smaWindowDays
        })),
      setEnabled: (mode, key, enabled) =>
        set((s) => ({
          [mode]: {
            ...s[mode],
            // Turning a line ON always resets `active` to true so the
            // chip comes back in its "line drawn" state. Turning OFF
            // leaves `active` alone — it doesn't matter while the chip
            // is gone, and preserving it means a quick toggle off/on
            // doesn't surprise the user.
            [key]: {
              enabled,
              active: enabled ? true : s[mode][key].active
            }
          }
        })),
      setActive: (mode, key, active) =>
        set((s) => ({
          [mode]: { ...s[mode], [key]: { ...s[mode][key], active } }
        })),
      toggleActive: (mode, key) =>
        set((s) => ({
          [mode]: {
            ...s[mode],
            [key]: { ...s[mode][key], active: !s[mode][key].active }
          }
        })),
      setAll: (mode, enabled) =>
        set((s) => {
          const next: ModeState = { ...s[mode] };
          (Object.keys(next) as LineKey[]).forEach((k) => {
            // Skip ND in pct — it doesn't have a UI switch there, so
            // bulk-toggling shouldn't flip a hidden setting.
            if (mode === "pct" && k === "netDeposits") return;
            next[k] = {
              enabled,
              active: enabled ? true : next[k].active
            };
          });
          return { [mode]: next };
        })
    }),
    {
      name: "whb-chart-settings",
      storage: createJSONStorage(() => localStorage),
      // Bump when the shape changes so stale persisted state gets
      // dropped rather than corrupting fresh defaults. v3 added the
      // SMA line + window settings (Sergey 2026-04-30).
      version: 3
    }
  )
);
